import { Modal } from '../Modal';
import { Button } from '../Button';

interface CancellationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  periodEnd: number | null;
}

export function CancellationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  periodEnd,
}: CancellationModalProps) {
  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'the end of your billing period';
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Cancel Subscription">
      <div className="space-y-4">
        <div className="bg-warning/10 border border-warning/20 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <span className="text-warning text-xl">⚠️</span>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-warning">Are you sure?</h3>
              <div className="mt-1 text-sm text-warning/80">
                Canceling your subscription will have the following effects:
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <span className="text-destructive mt-0.5">✗</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Access until {formatDate(periodEnd)}</p>
              <p className="text-xs text-muted-foreground mt-1">
                You'll retain full access to your subscription until the end of your current billing period.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-destructive mt-0.5">✗</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Read-only access after cancellation</p>
              <p className="text-xs text-muted-foreground mt-1">
                After {formatDate(periodEnd)}, your account will switch to read-only mode. You won't be able
                to create new identities or aliases.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-destructive mt-0.5">✗</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">Loss of premium features</p>
              <p className="text-xs text-muted-foreground mt-1">
                All premium features (webhooks, priority support, etc.) will be disabled.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <span className="text-success mt-0.5">✓</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-foreground">No further charges</p>
              <p className="text-xs text-muted-foreground mt-1">
                Your payment method will not be charged again after cancellation.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-info/10 border border-info/20 rounded-md p-3">
          <p className="text-xs text-info">
            <strong>Note:</strong> You can reactivate your subscription anytime before {formatDate(periodEnd)}{' '}
            to continue with the same plan and settings.
          </p>
        </div>

        <div className="flex gap-3 pt-4">
          <Button variant="secondary" onClick={onClose} disabled={isLoading} className="flex-1">
            Keep Subscription
          </Button>
          <Button
            onClick={onConfirm}
            isLoading={isLoading}
            className="flex-1 bg-destructive hover:bg-destructive/90 text-white"
          >
            Cancel Subscription
          </Button>
        </div>
      </div>
    </Modal>
  );
}
